(function () {
  const ROOT_SELECTOR = '[data-related-add-on-block]';
  const rootControllers = new WeakMap();

  function getShopifyRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function parseQuantity(rawQuantity) {
    const parsed = Number.parseInt(String(rawQuantity || '1'), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  function getRelatedAddOnQuantity(root) {
    const quantityInput = root.querySelector('[data-related-add-on-quantity]');
    if (quantityInput instanceof HTMLInputElement) {
      return parseQuantity(quantityInput.value);
    }

    return parseQuantity(root.getAttribute('data-default-related-quantity'));
  }

  function hideMessage(root, selector) {
    const element = root.querySelector(selector);
    if (!element) return;
    element.hidden = true;
    const messageTarget = element.querySelector('[data-related-add-on-warning-text]') || element;
    messageTarget.textContent = '';
  }

  function showMessage(root, selector, message) {
    const element = root.querySelector(selector);
    if (!element) return;
    element.hidden = false;
    const messageTarget = element.querySelector('[data-related-add-on-warning-text]') || element;
    messageTarget.textContent = message;
  }

  function setEditorNote(root, message) {
    const note = root.querySelector('[data-related-add-on-editor-note]');
    if (!note) return;
    note.hidden = !message;
    note.textContent = message || '';
  }

  function setFormBusy(form, isBusy) {
    form.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((button) => {
      if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) {
        button.disabled = isBusy;
      }
    });
  }

  function findSupportedForm(root) {
    const section = root.closest('.shopify-section') || root.closest('section') || root.closest('main') || document;
    const forms = section.querySelectorAll('form[action*="/cart/add"]');

    for (const form of forms) {
      const variantInput = form.querySelector('input[name="id"]');
      const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');

      if (variantInput && submitButton) {
        return form;
      }
    }

    return null;
  }

  function getCartContext() {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    return cart && typeof cart.renderContents === 'function' ? cart : null;
  }

  function toggleDynamicCheckout(form, hidden) {
    const wrapper = form.querySelector('.shopify-payment-button');

    if (!wrapper) {
      return;
    }

    wrapper.classList.toggle('pdp-related-add-on__payment-button--hidden', hidden);
    if (!wrapper.hasAttribute('data-related-add-on-original-aria-hidden')) {
      const existingAriaHidden = wrapper.getAttribute('aria-hidden');
      wrapper.setAttribute(
        'data-related-add-on-original-aria-hidden',
        existingAriaHidden == null ? '' : existingAriaHidden
      );
    }
    wrapper.setAttribute('aria-hidden', hidden ? 'true' : 'false');

    if ('inert' in wrapper) {
      if (!wrapper.hasAttribute('data-related-add-on-original-inert')) {
        wrapper.setAttribute('data-related-add-on-original-inert', wrapper.inert ? 'true' : 'false');
      }
      wrapper.inert = hidden;
    }

    wrapper.querySelectorAll('button, [role="button"], a, input, select, textarea').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      if (hidden) {
        if (!element.hasAttribute('data-related-add-on-original-disabled')) {
          const isDisabled =
            element instanceof HTMLButtonElement ||
            element instanceof HTMLInputElement ||
            element instanceof HTMLSelectElement ||
            element instanceof HTMLTextAreaElement
              ? element.disabled
              : element.getAttribute('aria-disabled') === 'true';
          element.setAttribute('data-related-add-on-original-disabled', isDisabled ? 'true' : 'false');
        }

        if (!element.hasAttribute('data-related-add-on-tabindex')) {
          const existingTabIndex = element.getAttribute('tabindex');
          element.setAttribute('data-related-add-on-tabindex', existingTabIndex == null ? '' : existingTabIndex);
        }

        element.setAttribute('tabindex', '-1');
        element.setAttribute('aria-disabled', 'true');

        if (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) {
          element.disabled = true;
        }
      } else {
        if (element.hasAttribute('data-related-add-on-tabindex')) {
          const previous = element.getAttribute('data-related-add-on-tabindex');
          if (previous === '') {
            element.removeAttribute('tabindex');
          } else if (previous != null) {
            element.setAttribute('tabindex', previous);
          }
          element.removeAttribute('data-related-add-on-tabindex');
        }

        const originallyDisabled = element.getAttribute('data-related-add-on-original-disabled') === 'true';
        if (!originallyDisabled) {
          element.removeAttribute('aria-disabled');
        } else {
          element.setAttribute('aria-disabled', 'true');
        }
        element.removeAttribute('data-related-add-on-original-disabled');

        if (
          element instanceof HTMLButtonElement ||
          element instanceof HTMLInputElement ||
          element instanceof HTMLSelectElement ||
          element instanceof HTMLTextAreaElement
        ) {
          element.disabled = originallyDisabled;
        }
      }
    });

    if (!hidden) {
      const originalAriaHidden = wrapper.getAttribute('data-related-add-on-original-aria-hidden');
      if (originalAriaHidden === '') {
        wrapper.removeAttribute('aria-hidden');
      } else if (originalAriaHidden != null) {
        wrapper.setAttribute('aria-hidden', originalAriaHidden);
      }
      wrapper.removeAttribute('data-related-add-on-original-aria-hidden');

      if ('inert' in wrapper) {
        wrapper.inert = wrapper.getAttribute('data-related-add-on-original-inert') === 'true';
        wrapper.removeAttribute('data-related-add-on-original-inert');
      }
    }
  }

  async function postCartAdd(items, cart) {
    const body = { items };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname + window.location.search;
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    const response = await fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    const parsed = await response.json().catch(() => null);
    return { response, parsed };
  }

  async function handleSuccessfulAdd(root, formData, parsed, cart) {
    if (!cart) {
      window.location.assign(window.routes?.cart_url || `${getShopifyRoot()}cart`);
      return;
    }

    if (cart.tagName === 'CART-NOTIFICATION' && !parsed.key && Array.isArray(parsed.items) && parsed.items[0]?.key) {
      parsed.key = parsed.items[0].key;
    }

    if (!parsed?.status && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      await publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'pdp-related-add-on',
        productVariantId: formData.get('id'),
        cartData: parsed,
      });
    }

    cart.renderContents(parsed);

    if (cart.classList.contains('is-empty')) {
      cart.classList.remove('is-empty');
    }
  }

  async function handleSubmit(event, root, form) {
    const checkbox = root.querySelector('input[type="checkbox"]');
    if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) {
      hideMessage(root, '[data-related-add-on-warning]');
      return;
    }

    const formData = new FormData(form);
    const mainVariantId = formData.get('id');
    if (!mainVariantId) {
      showMessage(
        root,
        '[data-related-add-on-warning]',
        root.getAttribute('data-variant-validation-note') || 'Select a valid product variant before adding the add-on.'
      );
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const addOnVariantId = root.getAttribute('data-related-variant-id');
    if (!addOnVariantId) return;

    const cart = getCartContext();
    const quantity = parseQuantity(formData.get('quantity'));
    const relatedAddOnQuantity = getRelatedAddOnQuantity(root);
    const addOnQuantity = quantity * relatedAddOnQuantity;
    const genericError =
      root.getAttribute('data-generic-error-message') ||
      'The add-on could not be added right now. Please try again.';
    const unavailableWarning =
      root.getAttribute('data-warning-message') ||
      'The main item was added, but the add-on became unavailable before it could be added.';

    hideMessage(root, '[data-related-add-on-warning]');
    setFormBusy(form, true);

    const combinedItems = [
      { id: String(mainVariantId), quantity },
      {
        id: String(addOnVariantId),
        quantity: addOnQuantity,
        properties: {
          _addon_source: 'pdp-block',
        },
      },
    ];

    try {
      const combined = await postCartAdd(combinedItems, cart);
      if (combined.response.ok && combined.parsed) {
        await handleSuccessfulAdd(root, formData, combined.parsed, cart);
        return;
      }

      const mainOnly = await postCartAdd([{ id: String(mainVariantId), quantity }], cart);
      if (mainOnly.response.ok && mainOnly.parsed) {
        showMessage(root, '[data-related-add-on-warning]', unavailableWarning);
        await handleSuccessfulAdd(root, formData, mainOnly.parsed, cart);
        return;
      }

      showMessage(root, '[data-related-add-on-warning]', genericError);
    } catch (error) {
      console.error(error);
      showMessage(root, '[data-related-add-on-warning]', genericError);
    } finally {
      setFormBusy(form, false);
    }
  }

  function setupBlock(root) {
    const existing = rootControllers.get(root);
    if (existing) {
      existing.abort();
    }

    const form = findSupportedForm(root);
    const checkbox = root.querySelector('input[type="checkbox"]');

    if (!(checkbox instanceof HTMLInputElement) || !form) {
      if (window.Shopify?.designMode) {
        setEditorNote(
          root,
          root.getAttribute('data-unsupported-theme-message') ||
            "This theme's product form is not supported by this version of the add-on block."
        );
        root.hidden = false;
        return;
      }

      root.hidden = true;
      return;
    }

    const submitTarget = form.closest('product-form') || form.parentElement || form;
    const controller = new AbortController();
    rootControllers.set(root, controller);
    setEditorNote(root, '');
    toggleDynamicCheckout(form, checkbox.checked);

    checkbox.addEventListener(
      'change',
      () => {
        hideMessage(root, '[data-related-add-on-warning]');
        toggleDynamicCheckout(form, checkbox.checked);
      },
      { signal: controller.signal }
    );

    submitTarget.addEventListener(
      'submit',
      (event) => {
        void handleSubmit(event, root, form);
      },
      {
        capture: true,
        signal: controller.signal,
      }
    );
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!(root instanceof HTMLElement) || root.dataset.relatedAddOnReady === 'true') return;

      root.dataset.relatedAddOnReady = 'true';
      setupBlock(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;

    event.target.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!(root instanceof HTMLElement)) return;

      const controller = rootControllers.get(root);
      if (controller) {
        controller.abort();
        rootControllers.delete(root);
      }

      delete root.dataset.relatedAddOnReady;
    });
  });
})();
